import React, { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ChartLineUp } from "@phosphor-icons/react";
import { formatBigCount, unixToDate } from "@/lib/format";
import { Panel, WindowTabs, Freshness, ErrorState } from "@/components/assets/parts";

const fmtDay = (t) => new Date(t * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const LegendDot = ({ color, dashed, children }) => (
  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] mono text-muted-foreground">
    <svg width="22" height="8" aria-hidden="true">
      <line x1="0" y1="4" x2="22" y2="4" stroke={color} strokeWidth="2.5" strokeDasharray={dashed ? "4 3" : undefined} strokeLinecap="round" />
    </svg>
    {children}
  </span>
);

const Tip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = (k) => payload.find((p) => p.dataKey === k)?.value;
  return (
    <div className="rounded-lg border border-primary/30 bg-card/95 backdrop-blur px-3 py-2 text-[11px] mono space-y-1">
      <div className="text-muted-foreground">{unixToDate(label)}</div>
      <div className="text-[#22d3ee]">Asset transactions: {Number(row("tx") || 0).toLocaleString("en-US")}</div>
      <div className="text-[#a855f7]">Active assets: {Number(row("active") || 0).toLocaleString("en-US")}</div>
      {row("prevTx") != null ? <div className="text-[#22d3ee]/60">Prev tx: {Number(row("prevTx")).toLocaleString("en-US")}</div> : null}
    </div>
  );
};

export const ActivityTrend = ({ daily = [], loading, error, reload, freshness }) => {
  const [range, setRange] = useState("7");

  const { dataset, hasPrev } = useMemo(() => {
    const pts = daily || [];
    if (range === "7") {
      const cur = pts.slice(-7);
      const prev = pts.length >= 14 ? pts.slice(-14, -7) : [];
      const ds = cur.map((p, i) => ({
        t: p.bucket_start,
        tx: p.tx_count,
        active: p.active_assets,
        prevTx: prev[i]?.tx_count ?? null,
        prevActive: prev[i]?.active_assets ?? null,
      }));
      return { dataset: ds, hasPrev: prev.length === cur.length && prev.length > 0 };
    }
    return { dataset: pts.map((p) => ({ t: p.bucket_start, tx: p.tx_count, active: p.active_assets })), hasPrev: false };
  }, [daily, range]);

  const right = (
    <div className="flex items-center gap-2 flex-wrap">
      <Freshness freshness={freshness} testid="trend-freshness" />
      <WindowTabs value={range} onChange={setRange} options={["7", "30"]} testid="trend-range" />
    </div>
  );

  return (
    <Panel title="Asset activity trend" icon={ChartLineUp} right={right} testid="activity-trend">
      <div className="px-5 pt-3 pb-1 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-muted-foreground/80">Daily native-asset activity — transactions and distinct active assets.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <LegendDot color="#22d3ee">Asset transactions</LegendDot>
          <LegendDot color="#a855f7">Active assets</LegendDot>
          {hasPrev ? <LegendDot color="#22d3ee" dashed>Previous period</LegendDot> : null}
        </div>
      </div>
      <div className="px-2 sm:px-4 py-4" data-testid="activity-trend-canvas">
        {error && !dataset.length ? (
          <ErrorState error={error} onRetry={reload} testid="trend-error" />
        ) : loading && !dataset.length ? (
          <div className="h-[300px] flex items-center justify-center text-[12px] text-muted-foreground mono animate-pulse">loading chart…</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dataset} margin={{ top: 8, right: 14, left: 2, bottom: 4 }}>
              <CartesianGrid stroke="rgba(148,163,184,0.1)" vertical={false} />
              <XAxis dataKey="t" tickFormatter={fmtDay} tick={{ fill: "#64748b", fontSize: 10, fontFamily: "monospace" }} axisLine={{ stroke: "rgba(148,163,184,0.2)" }} tickLine={false} minTickGap={20} />
              <YAxis tickFormatter={(v) => formatBigCount(v)} tick={{ fill: "#64748b", fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} width={44} />
              <Tooltip content={<Tip />} cursor={{ stroke: "rgba(34,211,238,0.25)" }} />
              {hasPrev ? (
                <Line type="monotone" dataKey="prevTx" stroke="#22d3ee" strokeOpacity={0.4} strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive animationDuration={700} />
              ) : null}
              {hasPrev ? (
                <Line type="monotone" dataKey="prevActive" stroke="#a855f7" strokeOpacity={0.4} strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive animationDuration={700} />
              ) : null}
              <Line type="monotone" dataKey="active" stroke="#a855f7" strokeWidth={2.5} dot={false} isAnimationActive animationDuration={900} animationEasing="ease-out" />
              <Line type="monotone" dataKey="tx" stroke="#22d3ee" strokeWidth={2.5} dot={false} isAnimationActive animationDuration={900} animationEasing="ease-out" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </Panel>
  );
};
